import { createContext, useContext, useEffect, useRef, useState } from 'react';
import api from '../api/client';

const AlertsContext = createContext({
  count: 0,
  alerts: [],
  toasts: [],
  dismissToast: () => {},
});

const POLL_INTERVAL_MS = 15000;
const TOAST_TTL_MS = 12000;
const NEW_ALERT_WINDOW_MS = 10 * 60 * 1000;

export function AlertsProvider({ children }) {
  const [alerts, setAlerts] = useState([]);
  const [toasts, setToasts] = useState([]);
  const seenRef = useRef(new Set());
  const initializedRef = useRef(false);

  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const pushToast = (alert) => {
    const id = `toast-${alert.id}-${Date.now()}`;
    setToasts((prev) => [...prev, { id, alert }]);
    setTimeout(() => dismissToast(id), TOAST_TTL_MS);
  };

  const fetchAlerts = async () => {
    try {
      const { data } = await api.get('/api/alerts?is_resolved=false');
      const list = Array.isArray(data) ? data : [];
      setAlerts(list);

      if (!initializedRef.current) {
        list.forEach((a) => seenRef.current.add(a.id));
        initializedRef.current = true;
        return;
      }

      list
        .filter((a) => !seenRef.current.has(a.id))
        .forEach((a) => {
          seenRef.current.add(a.id);
          const createdAt = a.created_at ? new Date(a.created_at).getTime() : 0;
          if (Date.now() - createdAt < NEW_ALERT_WINDOW_MS) {
            pushToast(a);
          }
        });
    } catch (e) {
      // ignore network errors; next poll will retry
    }
  };

  useEffect(() => {
    fetchAlerts();
    const iv = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, []);

  const count = alerts.filter((a) => !a.is_resolved).length;

  return (
    <AlertsContext.Provider value={{ count, alerts, toasts, dismissToast }}>
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlerts() {
  return useContext(AlertsContext);
}

export default AlertsContext;
